import React, { useState, useMemo } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { Text, Button } from 'rsuite';
import {
  PresentationCol,
  PresentationRow,
} from '../../../layout/PresentationLayout';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { buildPaymentApi } from '../../../../api';
import { resolvePaymentErrorMessage } from '../../../../api/payment/paymentErrors';
import {
  CARD_ERROR,
  STRIPE_PAYMENT_INTENT_STATUSES,
  VALIDATION_ERROR,
} from '../stripeConstants';
import { GENERIC_PAYMENT_ERROR } from '../../constants';
import { useSelector } from 'react-redux';
import {
  selectCurrentIDToken,
  selectCurrentUserEmail,
} from '../../../../store/slices/auth/authSelectors';
import { selectPciPaymentApiEnabled } from '../../../../store/slices/config/configSelectors';
import {
  selectPaymentFees,
  selectPaymentID,
  selectPaymentSignatory,
  selectSelectedPaymentMethod,
} from '../../../../store/slices/payment/paymentSelectors';
import Notice from '../../../user-feedback/Notice';
import IconIndicator from '../../../user-feedback/IconIndicator';
import { calculateTotalDue } from '../../common/utils';
import { convertNumberToCurrency } from '../../../../common/utils';
import { getPaymentCategory } from '../../../../common/paymentUtils';
import { FLOWS_ENUM } from '../../../../common/enums';

interface CreditCardProps {
  flow: FLOWS_ENUM;
  onSuccess: () => void;
  permitNumbers: string[];
}

const CreditCard = ({ flow, onSuccess, permitNumbers }: CreditCardProps) => {
  const stripe = useStripe();
  const elements = useElements();

  const currentUserEmail = useSelector(selectCurrentUserEmail);
  const authToken = useSelector(selectCurrentIDToken);
  const paymentID = useSelector(selectPaymentID);
  const pciEnabled = useSelector(selectPciPaymentApiEnabled);
  const signatoryName = useSelector(selectPaymentSignatory);
  const selectedPaymentMethod = useSelector(selectSelectedPaymentMethod);
  const paymentFees = useSelector(selectPaymentFees);
  const apiAccess = useMemo(() => buildPaymentApi(pciEnabled), [pciEnabled]);

  const [paymentInProgress, setPaymentsInProgress] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const payButtonDisabled = useMemo(
    () => paymentInProgress || !stripe || !elements,
    [elements, paymentInProgress, stripe]
  );
  const totalDue = useMemo(
    () => calculateTotalDue(selectedPaymentMethod, paymentFees),
    [selectedPaymentMethod, paymentFees]
  );

  const paymentClick = async () => {
    if (payButtonDisabled) {
      return;
    }

    setPaymentsInProgress(true);
    setErrorMessage('');

    try {
      // Register payment in server, who responds with client secret so we can proceed with payment
      const { clientSecret } = await apiAccess.payByCreditCard(
        {
          email: currentUserEmail ?? '',
          payment_category: getPaymentCategory(flow)!,
          payment_id: Number(paymentID),
          signatory: signatoryName ?? '',
          subtotal: paymentFees?.summary?.total ?? 0,
          signature: paymentFees?.signature ?? '',
          credits: paymentFees?.summary?.credit ?? 0,
          permit_numbers: permitNumbers,
        },
        authToken
      );

      // Make card payment directly with stripe. Requires client_secret from backend Stripe code
      // This object will have either an error or paymentIntent property
      const result = await stripe!.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements!.getElement(CardNumberElement)!,
          billing_details: { name: signatoryName ?? '' },
        },
        receipt_email: currentUserEmail,
      });

      // Check for errors in stripe transaction
      if (result.error) {
        let message = GENERIC_PAYMENT_ERROR;
        /*
         * Errors can be card_error, api_error, idempotency_error, invalid_request_error.
         * Here we only display the nice `error.message` msg for card_error. Else we want
         * the user to see our own custom error message string
         */
        if (
          result.error.type === CARD_ERROR ||
          result.error.type === VALIDATION_ERROR
        ) {
          message = result.error.message ?? GENERIC_PAYMENT_ERROR;
        }

        setErrorMessage(message);
        setPaymentsInProgress(false);
      } else if (
        result.paymentIntent?.status === STRIPE_PAYMENT_INTENT_STATUSES.SUCCESS
      ) {
        // The card is charged at this point. onSuccess checks the payment
        // status and then navigates away, so the button has to stay disabled
        // until this component unmounts.
        onSuccess();
      } else {
        setPaymentsInProgress(false);
      }
    } catch (err) {
      setErrorMessage(resolvePaymentErrorMessage(err, GENERIC_PAYMENT_ERROR));
      datadogRum.addError(err);
      setPaymentsInProgress(false);
    }
  };

  return (
    <div>
      <div
        className="mb-16"
        role="group"
        aria-labelledby="label-stripe-card-number"
      >
        <Text
          id="label-stripe-card-number"
          className="text-small mb-4"
        >
          Card Number
        </Text>
        <CardNumberElement
          id="stripe-card-number"
          className="stripe-element"
          options={{ showIcon: true }}
        />
      </div>
      <PresentationRow className="mb-16">
        <PresentationCol
          xs={24}
          md={12}
          className="mb-16"
        >
          <div
            role="group"
            aria-labelledby="label-stripe-expiry"
          >
            <Text
              id="label-stripe-expiry"
              className="text-small mb-4"
            >
              Expiry Date
            </Text>
            <CardExpiryElement
              className="stripe-element"
              id="stripe-expiry"
            />
          </div>
        </PresentationCol>
        <PresentationCol
          xs={24}
          md={12}
        >
          <div
            role="group"
            aria-labelledby="label-stripe-cvc"
          >
            <Text
              id="label-stripe-cvc"
              className="text-small mb-4"
            >
              CVC
            </Text>
            <CardCvcElement
              className="stripe-element"
              id="stripe-cvc"
            />
          </div>
        </PresentationCol>
      </PresentationRow>
      {errorMessage && (
        <div className="mb-16">
          <Notice message={errorMessage} />
        </div>
      )}
      {paymentInProgress && (
        <div className="mb-16">
          <IconIndicator />
        </div>
      )}
      <Button
        className="btn-primary full-width"
        disabled={payButtonDisabled}
        onClick={paymentClick}
      >
        Pay {convertNumberToCurrency(totalDue)}
      </Button>
    </div>
  );
};

export default CreditCard;
